import { API_BASE_URL } from '~lib/api/config';

import type { DatasetSearchResponse } from './types';

export interface SearchDatasetsArgs {
  query: string;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}

/** Call `GET /datasets/search` and return the parsed response. */
export async function searchDatasets({
  query,
  page = 1,
  limit = 10,
  signal,
}: SearchDatasetsArgs): Promise<DatasetSearchResponse> {
  // Empty `API_BASE_URL` ⇒ same origin as the SPA. Resolve against the
  // document origin so `URL` doesn't throw on a relative base.
  const base = API_BASE_URL || window.location.origin;
  const url = new URL('/datasets/search', base);
  url.searchParams.set('query', query);
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    const detail = await safeReadProblemDetail(response);
    throw new SearchError(response.status, detail ?? response.statusText);
  }

  return (await response.json()) as DatasetSearchResponse;
}

export class SearchError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SearchError';
    this.status = status;
  }
}

async function safeReadProblemDetail(response: Response): Promise<string | null> {
  try {
    const body = await response.json();
    if (body && typeof body === 'object') {
      // RFC 9457 Problem Details: prefer `detail`, fall back to `title`.
      return (body as { detail?: string; title?: string }).detail ??
        (body as { title?: string }).title ??
        null;
    }
  } catch {
    /* fall through */
  }
  return null;
}
